import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useState } from 'react';
import { z } from 'zod';

// Zod schemas for RxNorm API responses
const rxNormDrugSchema = z.object({
  rxcui: z.string(),
  name: z.string(),
  synonym: z.string().optional(),
  tty: z.string().optional(), // Term type (e.g., SBD = Semantic Branded Drug, SCD = Semantic Clinical Drug)
});

export type RxNormDrug = z.infer<typeof rxNormDrugSchema>;

const rxNormApproximateResponseSchema = z.object({
  approximateGroup: z
    .object({
      candidate: z
        .array(
          z.object({
            rxcui: z.string(),
            score: z.string(),
            rank: z.string(),
            name: z.string().optional(),
          }),
        )
        .optional(),
    })
    .optional(),
});

const rxNormPropertiesResponseSchema = z.object({
  properties: z
    .object({
      rxcui: z.string(),
      name: z.string(),
      synonym: z.string().optional(),
      tty: z.string().optional(),
    })
    .optional(),
});

const RXNORM_BASE_URL = 'https://rxnav.nlm.nih.gov/REST';
const DEBOUNCE_MS = 300;
const MAX_RESULTS = 10;

/**
 * Fetches drug properties from RxNorm by rxcui
 */
const fetchDrugProperties = async (
  rxcui: string,
): Promise<RxNormDrug | null> => {
  try {
    const response = await fetch(
      `${RXNORM_BASE_URL}/rxcui/${rxcui}/properties.json`,
    );
    const json = await response.json();
    const parseResult = rxNormPropertiesResponseSchema.safeParse(json);

    if (!parseResult.success) {
      return null;
    }

    const { properties } = parseResult.data;
    if (properties) {
      return {
        rxcui: properties.rxcui,
        name: properties.name,
        synonym: properties.synonym,
        tty: properties.tty,
      };
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Searches RxNorm for drugs matching the search term
 */
const searchRxNorm = async (term: string): Promise<RxNormDrug[]> => {
  if (!term || term.length < 2) {
    return [];
  }

  try {
    // Use approximate term search for fuzzy matching (best for typeahead)
    const response = await fetch(
      `${RXNORM_BASE_URL}/approximateTerm.json?term=${encodeURIComponent(term)}&maxEntries=${MAX_RESULTS}`,
    );
    const json = await response.json();
    const parseResult = rxNormApproximateResponseSchema.safeParse(json);

    if (!parseResult.success) {
      return [];
    }

    const candidates = parseResult.data.approximateGroup?.candidate ?? [];

    // Fetch full properties for each candidate in parallel
    const drugPromises = candidates.map((candidate) =>
      fetchDrugProperties(candidate.rxcui),
    );

    const drugs = await Promise.all(drugPromises);

    // Filter out nulls and duplicates by name
    const seenNames = new Set<string>();
    return drugs.filter((drug): drug is RxNormDrug => {
      if (!drug) return false;
      const normalizedName = drug.name.toLowerCase();
      if (seenNames.has(normalizedName)) return false;
      seenNames.add(normalizedName);
      return true;
    });
  } catch {
    return [];
  }
};

export interface UseRxNormSearchResult {
  results: RxNormDrug[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Hook for searching RxNorm drug database with debouncing
 */
export const useRxNormSearch = (searchTerm: string): UseRxNormSearchResult => {
  const [results, setResults] = useState<RxNormDrug[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, DEBOUNCE_MS);

  useEffect(() => {
    const performSearch = async () => {
      if (!debouncedSearchTerm || debouncedSearchTerm.length < 2) {
        setResults([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const searchResults = await searchRxNorm(debouncedSearchTerm);
        setResults(searchResults);
      } catch {
        setError('Failed to search medications');
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedSearchTerm]);

  // Show loading state when user is typing but debounce hasn't fired yet
  const showLoading =
    isLoading || (searchTerm.length >= 2 && searchTerm !== debouncedSearchTerm);

  return {
    results,
    isLoading: showLoading,
    error,
  };
};
